import { custom } from 'devextreme/ui/dialog';
import useApi from '../../hooks/useApi';
import { useCallback } from 'react';
import { eachDayOfInterval, startOfDay } from 'date-fns';
import Guid from '../../models/Guid';
import IEnregistrementAccompagnateur from '../../models/IEnregistrementAccompagnateur';
import IGestionEnregistrement from '../../models/IGestionEnregistrement';
import { Capture, CaptureDetails, EnregistrementDetails, IActiviteEnregistrement, IDatesParticipation, IEnregistrementDetails, IParticipant } from './type';
import { Mode } from '../../hooks/useCustomDataSource';

/**
 * Cette fonction affiche une boîte de dialogue personnalisée avec un titre et un message spécifiés.
 * La boîte de dialogue propose deux boutons : "Oui" et "Non".
 * Lorsqu'un bouton est cliqué, le texte du bouton cliqué est retourné.
 * Si le bouton "Oui" est cliqué, une action spécifique pourrait être effectuée (à compléter dans le bloc conditionnel).
 * 
 * @param {string} title - Le titre de la boîte de dialogue.
 * @param {string} message - Le message à afficher dans la boîte de dialogue.
 */
export const alertEnregistrement = (title:string, message:string ) => {
  let myDialog = custom({
      dragEnabled: true,
      title: title,
      messageHtml: message,
      buttons: [{
        text: "Oui",
        onClick: (e) => {
          return { buttonText: e.component.option("text") };
        }
      }, {
        text: "Non",
        onClick: (e) => {
          return { buttonText: e.component.option("text") };
        }
      }]
  });
  
  myDialog.show().then(async (dialogResult: any) => {
      if (dialogResult.buttonText === "Oui") {
          // Action à effectuer si "Oui" est sélectionné
      }
  });
};

/**
 * Ce hook personnalisé utilise fetchData pour récupérer des données depuis un chemin donné
 * et met à jour l'état avec les résultats.
 * 
 * @returns {object} - Retourne un objet contenant la fonction `fetchAndSetData` pour récupérer et
 *                     mettre à jour les données.
 */
export const useFetchData = () => {
  const { fetchData } = useApi();

  const fetchAndSetData = useCallback(async (path: string, setData: React.Dispatch<React.SetStateAction<any>>) => {
    try {
      const result = await fetchData("get", { customPath: path });
      setData(result);
    } catch (error) {
      console.log(error);
    }
  }, []);

  return { fetchAndSetData };
};

export const getActiviteRowData = (cellData: { data?: { data?: unknown } & Record<string, unknown> }) =>
  (cellData?.data?.data ?? cellData?.data ?? cellData) as { datesParticipation?: IDatesParticipation[] };

interface BuildDatesParticipationParams {
  dateArrive: Date;
  dateDepart: Date;
  idEnregistrementEntete: Guid;
  idEnregistrementDetail: Guid;
  idClient: Guid;
  clientNom: string;
  clientPrenom: string;
  accompagnateurs?: IEnregistrementAccompagnateur[];
  existingDates?: IDatesParticipation[];
}

export const isParticipationDateInRange = (date: Date, dateArrive: Date, dateDepart: Date) => {
  const participationDay = startOfDay(new Date(date)).getTime();
  const start = startOfDay(new Date(dateArrive)).getTime();
  const end = startOfDay(new Date(dateDepart)).getTime();
  return participationDay >= start && participationDay <= end;
};

const getParticipationDayKey = (date: Date | string) => startOfDay(new Date(date)).getTime();

export interface IParticipantIdentity {
  nom: string;
  prenom: string;
  idAccompagnateur?: string | Guid;
}

const estIdAccompagnateurVide = (id?: string | Guid) => {
  const value = String(id ?? '');
  return !value || value === Guid.empty.toString();
};

export const estMemeParticipant = (
  participant: Pick<IParticipant, 'nom' | 'prenom' | 'idAccompagnateur'>,
  identity: IParticipantIdentity,
) => {
  if (participant.nom !== identity.nom || participant.prenom !== identity.prenom) {
    return false;
  }

  if (String(participant.idAccompagnateur ?? '') === String(identity.idAccompagnateur ?? '')) {
    return true;
  }

  return estIdAccompagnateurVide(participant.idAccompagnateur)
    !== estIdAccompagnateurVide(identity.idAccompagnateur);
};

const findExistingDateForDay = (
  existingDates: IDatesParticipation[] | undefined,
  day: Date,
): IDatesParticipation | undefined => {
  const dayKey = getParticipationDayKey(day);
  return existingDates?.find((entry) => !entry.isDeleted && getParticipationDayKey(entry.dateParticipation) === dayKey)
    ?? existingDates?.find((entry) => entry.isDeleted && getParticipationDayKey(entry.dateParticipation) === dayKey);
};

const dedupeDatesParticipationByDay = (dates: IDatesParticipation[]): IDatesParticipation[] => {
  const byDay = new Map<number, IDatesParticipation>();

  dates.forEach((entry) => {
    const dayKey = getParticipationDayKey(entry.dateParticipation);
    const existing = byDay.get(dayKey);

    if (!existing) {
      byDay.set(dayKey, entry);
      return;
    }

    if (existing.isDeleted && !entry.isDeleted) {
      byDay.set(dayKey, entry);
      return;
    }

    if (!existing.isDeleted && entry.isDeleted) {
      return;
    }

    const existingHasPersistedId = existing.idEnregistrementDetailDate && !existing.idEnregistrementDetailDate.isEmpty();
    const entryHasPersistedId = entry.idEnregistrementDetailDate && !entry.idEnregistrementDetailDate.isEmpty();
    if (!existingHasPersistedId && entryHasPersistedId) {
      byDay.set(dayKey, entry);
    }
  });

  return Array.from(byDay.values());
};

const markOutOfRangeDatesAsDeleted = (
  existingDates: IDatesParticipation[],
  dateArrive: Date,
  dateDepart: Date,
): IDatesParticipation[] => (
  existingDates
    .filter((entry) => !entry.isDeleted && !isParticipationDateInRange(entry.dateParticipation, dateArrive, dateDepart))
    .map((entry) => ({
      ...entry,
      isDeleted: true,
      participants: entry.participants?.map((participant) => ({ ...participant, isDeleted: true })),
    }))
);

const createParticipant = (
  participant: Pick<IParticipant, 'nom' | 'prenom' | 'idClient' | 'idAccompagnateur'>,
  ids: Pick<IParticipant, 'idEnregistrementEntete' | 'idEnregistrementDetail' | 'idEnregistrementDetailDate'>,
  existingParticipant?: IParticipant,
): IParticipant => ({
  prenom: participant.prenom,
  nom: participant.nom,
  exclus: existingParticipant?.exclus ?? false,
  actif: true,
  dateCreation: new Date(),
  dateModification: new Date(),
  dateSuppression: null,
  idEnregistrementEntete: ids.idEnregistrementEntete,
  idEnregistrementDetail: ids.idEnregistrementDetail,
  idEnregistrementDetailDate: ids.idEnregistrementDetailDate,
  idClient: participant.idClient,
  idAccompagnateur: existingParticipant?.idAccompagnateur ?? participant.idAccompagnateur,
  idEnregistrementDetailParticipant: existingParticipant?.idEnregistrementDetailParticipant ?? Guid.newTemporary(),
  idUsagerCreation: Guid.empty,
  idUsagerModification: Guid.empty,
  idUsagerSuppression: null,
  isDeleted: false,
});

export const isValidAccompagnateur = (accompagnateur: IEnregistrementAccompagnateur) =>
  !accompagnateur.isDeleted
  && Boolean(accompagnateur.nom?.trim() || accompagnateur.prenom?.trim());

export const buildDatesParticipation = ({
  dateArrive,
  dateDepart,
  idEnregistrementEntete,
  idEnregistrementDetail,
  idClient,
  clientNom,
  clientPrenom,
  accompagnateurs = [],
  existingDates,
}: BuildDatesParticipationParams): IDatesParticipation[] => {
  const start = startOfDay(new Date(dateArrive));
  const end = startOfDay(new Date(dateDepart));
  if (start > end) {
    return markOutOfRangeDatesAsDeleted(existingDates ?? [], dateArrive, dateDepart);
  }

  const participantTemplates = [
    {
      nom: clientNom,
      prenom: clientPrenom,
      idClient,
      idAccompagnateur: Guid.empty,
    },
    ...accompagnateurs
      .filter(isValidAccompagnateur)
      .map((accompagnateur) => ({
        nom: accompagnateur.nom ?? '',
        prenom: accompagnateur.prenom ?? '',
        idClient: accompagnateur.idClient ?? idClient,
        idAccompagnateur: accompagnateur.idAccompagnateur ?? Guid.empty,
      })),
  ];

  const inRangeDates = eachDayOfInterval({ start, end }).map((date) => {
    const existingDate = findExistingDateForDay(existingDates, date);
    const idEnregistrementDetailDate = existingDate?.idEnregistrementDetailDate ?? Guid.newTemporary();

    return {
      dateParticipation: date,
      actif: true,
      dateCreation: existingDate?.dateCreation ?? new Date(),
      dateModification: new Date(),
      dateSuppression: null,
      idEnregistrementEntete,
      idEnregistrementDetail,
      idEnregistrementDetailDate,
      idUsagerCreation: existingDate?.idUsagerCreation ?? Guid.empty,
      idUsagerModification: existingDate?.idUsagerModification ?? Guid.empty,
      idUsagerSuppression: null,
      isDeleted: false,
      participants: participantTemplates.map((participant) => {
        const existingParticipant = existingDate?.participants.find((entry) =>
          estMemeParticipant(entry, participant)
        );

        return createParticipant(
          participant,
          { idEnregistrementEntete, idEnregistrementDetail, idEnregistrementDetailDate },
          existingParticipant,
        );
      }),
    };
  });

  const outOfRangeDeleted = markOutOfRangeDatesAsDeleted(existingDates ?? [], dateArrive, dateDepart);
  const inRangeDayKeys = new Set(inRangeDates.map((entry) => getParticipationDayKey(entry.dateParticipation)));

  return dedupeDatesParticipationByDay([
    ...inRangeDates,
    ...outOfRangeDeleted.filter((entry) => !inRangeDayKeys.has(getParticipationDayKey(entry.dateParticipation))),
  ]);
};

export const removeAccompagnateurDesDetailsEnregistrement = (
  details: IEnregistrementDetails[],
  accompagnateur: Pick<IEnregistrementAccompagnateur, 'nom' | 'prenom' | 'idAccompagnateur'>,
): IEnregistrementDetails[] => {
  const identity: IParticipantIdentity = {
    nom: accompagnateur.nom ?? '',
    prenom: accompagnateur.prenom ?? '',
    idAccompagnateur: accompagnateur.idAccompagnateur,
  };

  return details.map((detail) => ({
    ...detail,
    datesParticipation: detail.datesParticipation?.map((dateEntry) => ({
      ...dateEntry,
      participants: dateEntry.participants?.filter((participant) => !estMemeParticipant(participant, identity)) ?? [],
    })),
  }));
};

export const conserverParticipantActivite = (
  participant: Pick<IParticipant, 'nom' | 'prenom' | 'idAccompagnateur' | 'idClient'>,
  idClient: Guid,
  clientNom: string,
  clientPrenom: string,
  accompagnateurs: IEnregistrementAccompagnateur[] = [],
) => {
  if (!participant.nom?.trim() && !participant.prenom?.trim()) {
    return false;
  }

  const activeAccompagnateurs = accompagnateurs.filter(isValidAccompagnateur);
  if (activeAccompagnateurs.some((accompagnateur) =>
    estMemeParticipant(participant, {
      nom: accompagnateur.nom ?? '',
      prenom: accompagnateur.prenom ?? '',
      idAccompagnateur: accompagnateur.idAccompagnateur,
    })
  )) {
    return true;
  }

  if (!estIdAccompagnateurVide(participant.idAccompagnateur)) {
    return false;
  }

  if (idClient && !idClient.isEmpty() && participant.idClient && !participant.idClient.equals(idClient)) {
    return false;
  }

  return participant.nom === clientNom && participant.prenom === clientPrenom;
};

export const filtrerParticipantsDatesParticipation = (
  dates: IDatesParticipation[] | undefined,
  idClient: Guid,
  clientNom: string,
  clientPrenom: string,
  accompagnateurs: IEnregistrementAccompagnateur[] = [],
): IDatesParticipation[] => (
  dates ?? []
).map((date) => ({
  ...date,
  participants: date.participants?.filter((participant) =>
    conserverParticipantActivite(participant, idClient, clientNom, clientPrenom, accompagnateurs)
  ) ?? [],
}));

export const syncDatesParticipationForEnregistrement = (
  enregistrement: IGestionEnregistrement,
  clientNom: string,
  clientPrenom: string,
): IEnregistrementDetails[] => (
  enregistrement.enregistrementDetails || []
).map((detail) => {
  if (detail.isDeleted || !enregistrement.idEnregistrementEntete) return detail;

  return {
    ...detail,
    dateDebut: enregistrement.dateArrive,
    dateFin: enregistrement.dateDepart,
    datesParticipation: filtrerParticipantsDatesParticipation(
      buildDatesParticipation({
        dateArrive: enregistrement.dateArrive,
        dateDepart: enregistrement.dateDepart,
        idEnregistrementEntete: enregistrement.idEnregistrementEntete,
        idEnregistrementDetail: detail.idEnregistrementDetail,
        idClient: enregistrement.idClient,
        clientNom,
        clientPrenom,
        accompagnateurs: enregistrement.accompagnateurs,
        existingDates: detail.datesParticipation,
      }),
      enregistrement.idClient,
      clientNom,
      clientPrenom,
      enregistrement.accompagnateurs,
    ),
  };
});

const isParticipantExcluded = (participant: { exclus?: boolean | string | number | null }) =>
  participant.exclus === true || participant.exclus === 'true' || participant.exclus === 1;

export const isDateExcludedInParticipation = (
  datesParticipation: IDatesParticipation[] | undefined,
  date: Date,
  identity: IParticipantIdentity,
) => datesParticipation?.some((entry) =>
  !entry.isDeleted
  && startOfDay(new Date(entry.dateParticipation)).getTime() === startOfDay(date).getTime()
  && entry.participants?.some((participant) =>
    estMemeParticipant(participant, identity) && isParticipantExcluded(participant)
  )
) ?? false;

export const shouldHideExcludedParticipationDates = (mode: Mode) => mode !== Mode.Modifier;

export const toggleParticipantExclusion = (
  detail: IEnregistrementDetails,
  date: Date,
  identity: IParticipantIdentity,
): IEnregistrementDetails => ({
  ...detail,
  datesParticipation: detail.datesParticipation?.map((dateEntry) => {
    if (startOfDay(new Date(dateEntry.dateParticipation)).getTime() !== startOfDay(date).getTime()) {
      return dateEntry;
    }
    return {
      ...dateEntry,
      participants: dateEntry.participants?.map((participant) => {
        if (!estMemeParticipant(participant, identity)) {
          return participant;
        }
        return { ...participant, exclus: !participant.exclus };
      }),
    };
  }),
});

export const setAllParticipantExclusions = (
  detail: IEnregistrementDetails,
  identity: IParticipantIdentity,
  excluded: boolean,
): IEnregistrementDetails => ({
  ...detail,
  datesParticipation: detail.datesParticipation?.map((dateEntry) => ({
    ...dateEntry,
    participants: dateEntry.participants?.map((participant) => {
      if (!estMemeParticipant(participant, identity)) {
        return participant;
      }
      return { ...participant, exclus: excluded };
    }),
  })),
});

export const appliquerParticipantExclusions = (
  details: IEnregistrementDetails[],
  exclusions: { date: Date; identity: IParticipantIdentity }[]
): IEnregistrementDetails[] => {
  return details.map((detail) => ({
    ...detail,
    datesParticipation: detail.datesParticipation?.map((dateEntry) => ({
      ...dateEntry,
      participants: dateEntry.participants?.map((participant) => {
        const participationDate = startOfDay(new Date(dateEntry.dateParticipation));
        const isExcluded = exclusions.some(
          (exclusion) =>
            estMemeParticipant(participant, exclusion.identity)
            && startOfDay(exclusion.date).getTime() === participationDate.getTime()
        );
        return { ...participant, exclus: isExcluded };
      }),
    })),
  }));
};

const GUID_VIDE = '00000000-0000-0000-0000-000000000000';

export const dedupliquerCaptures = (captures: Capture[]): Capture[] => {
  const idsVus = new Set<string>();
  return captures.filter((capture) => {
    const id = String(capture.idCapture);
    if (idsVus.has(id)) {
      return false;
    }
    idsVus.add(id);
    return true;
  });
};

const resoudreIdEnregistrementDetailCapture = (
  capture: Capture,
  wrappers: EnregistrementDetails[] | undefined,
): string | null => {
  const id = String(capture.idEnregistrementDetail ?? '');
  if (id && id !== 'undefined' && id !== GUID_VIDE) {
    return id;
  }

  for (const wrapper of wrappers ?? []) {
    if ((wrapper.captures ?? []).some((item) => String(item.idCapture) === String(capture.idCapture))) {
      return String(wrapper.idEnregistrementDetail);
    }
  }

  return null;
};

export const synchroniserDetailsCapture = (
  details: IEnregistrementDetails[] | undefined,
  detailsCapture: EnregistrementDetails[] | undefined,
  activites: IActiviteEnregistrement[] | undefined,
): EnregistrementDetails[] => {
  const wrapperParDetailId = new Map(
    (detailsCapture ?? [])
      .filter((wrapper) => !wrapper.isDeleted)
      .map((wrapper) => [String(wrapper.idEnregistrementDetail), wrapper]),
  );

  const captureParId = new Map<string, Capture>();
  for (const wrapper of detailsCapture ?? []) {
    for (const capture of wrapper.captures ?? []) {
      captureParId.set(String(capture.idCapture), capture);
    }
  }

  const detailsActifs = (details ?? []).filter((detail) => !detail.isDeleted);

  return detailsActifs.map((detail) => {
    const idDetail = String(detail.idEnregistrementDetail);
    const existant = wrapperParDetailId.get(idDetail);
    const activite = activites?.find((a) => String(a.id) === String(detail.idActivite));

    const captures = dedupliquerCaptures(
      [...captureParId.values()]
        .filter((capture) => resoudreIdEnregistrementDetailCapture(capture, detailsCapture) === idDetail)
        .map((capture) => assurerEmplacementAvecAuMoinsUnDetail({
          ...capture,
          idEnregistrementDetail: idDetail,
          capturesDetails: [...(capture.capturesDetails ?? [])],
        }, { onlyIfTemporary: true })),
    );

    if (existant) {
      return {
        ...existant,
        idEnregistrementDetail: idDetail,
        idActivite: String(detail.idActivite),
        idSecteur: String(detail.idSecteur),
        idPointEau: String(detail.idPointEau),
        peche: activite?.peche ?? existant.peche,
        chasse: activite?.chasse ?? existant.chasse,
        activite: activite?.description ?? existant.activite,
        captures,
        isDeleted: false,
      };
    }

    return {
      idEnregistrementDetail: idDetail,
      idActivite: String(detail.idActivite),
      idSecteur: String(detail.idSecteur),
      idPointEau: String(detail.idPointEau),
      peche: activite?.peche ?? false,
      chasse: activite?.chasse ?? false,
      activite: activite?.description ?? '',
      secteur: '',
      pointEau: '',
      nombreJours: 0,
      nombrePersonnes: 0,
      prises: 0,
      captures,
      isDeleted: false,
    };
  });
};

export const calculerPrisesDetail = (detail: Partial<CaptureDetails>): number =>
  (detail.completQuantite ?? 0)
  + (detail.eviscereQuantite ?? 0)
  + (detail.eteteQuantite ?? 0)
  + (detail.consommeQuantite ?? 0)
  + (detail.nombreCaptureConsommee ?? 0)
  + (detail.nombreCaptureRapportee ?? 0)
  + (detail.nonPeseeQuantite ?? 0);

export const calculerPrisesEmplacement = (capture: Capture): number =>
  (capture.capturesDetails ?? [])
    .filter((detail) => !detail.isDeleted)
    .reduce((total, detail) => total + calculerPrisesDetail(detail), 0);

export const compterDetailsCaptureActifs = (capture: Capture | undefined): number =>
  (capture?.capturesDetails ?? []).filter((detail) => !detail.isDeleted).length;

export const emplacementPossedeAuMoinsUnDetail = (capture: Capture): boolean =>
  compterDetailsCaptureActifs(capture) > 0;

export const estIdCaptureTemporaire = (idCapture: string | undefined): boolean =>
  String(idCapture ?? '').toLowerCase().startsWith('ffffffff');

export const assurerEmplacementAvecAuMoinsUnDetail = (
  capture: Capture,
  { onlyIfTemporary = false }: { onlyIfTemporary?: boolean } = {},
): Capture => {
  if (onlyIfTemporary && !estIdCaptureTemporaire(String(capture.idCapture))) {
    return capture;
  }

  if (emplacementPossedeAuMoinsUnDetail(capture)) {
    return capture;
  }

  const idCapture = String(capture.idCapture);
  const capturesDetails = [
    creerDetailCaptureVide(idCapture),
    ...(capture.capturesDetails ?? []),
  ];

  return {
    ...capture,
    capturesDetails,
    prises: calculerPrisesEmplacement({ ...capture, capturesDetails }),
  };
};

export const fusionnerEmplacement = (existant: Capture, misAJour: Partial<Capture>): Capture =>
  assurerEmplacementAvecAuMoinsUnDetail({
    ...existant,
    ...misAJour,
    capturesDetails: existant.capturesDetails ?? misAJour.capturesDetails ?? [],
    prises: calculerPrisesEmplacement({
      ...existant,
      ...misAJour,
      capturesDetails: existant.capturesDetails ?? misAJour.capturesDetails ?? [],
    }),
  });

export const validerEmplacementsOntAuMoinsUnDetail = (
  detailsCapture: EnregistrementDetails[] | undefined,
): string | null => {
  for (const detail of detailsCapture ?? []) {
    const emplacementsActifs = filtrerCapturesActivesPourDetail(
      detail.captures,
      detail.idEnregistrementDetail,
    );

    if (emplacementsActifs.some((emplacement) => !emplacementPossedeAuMoinsUnDetail(emplacement))) {
      return 'Chaque emplacement doit contenir au moins un détail.';
    }
  }

  return null;
};

export const filtrerCapturesPourDetail = (
  captures: Capture[] | undefined,
  idEnregistrementDetail: string,
): Capture[] =>
  (captures ?? []).filter(
    (capture) => String(capture.idEnregistrementDetail) === String(idEnregistrementDetail),
  );

export const filtrerCapturesActivesPourDetail = (
  captures: Capture[] | undefined,
  idEnregistrementDetail: string,
): Capture[] =>
  filtrerCapturesPourDetail(captures, idEnregistrementDetail).filter((capture) => !capture.isDeleted);

export const mettreAJourDetailsCapture = (
  detailsCapture: EnregistrementDetails[],
  idEnregistrementDetail: string,
  updater: (detail: EnregistrementDetails) => EnregistrementDetails,
  detailParDefaut?: EnregistrementDetails,
): EnregistrementDetails[] => {
  const index = detailsCapture.findIndex(
    (detail) => String(detail.idEnregistrementDetail) === String(idEnregistrementDetail),
  );

  if (index === -1) {
    if (!detailParDefaut) {
      return detailsCapture;
    }

    return [...detailsCapture, updater(detailParDefaut)];
  }

  return detailsCapture.map((detail, detailIndex) => {
    if (detailIndex !== index) {
      return detail;
    }

    const detailMisAJour = updater({
      ...detail,
      captures: filtrerCapturesPourDetail(detail.captures, idEnregistrementDetail),
    });

    return {
      ...detailMisAJour,
      captures: filtrerCapturesPourDetail(detailMisAJour.captures, idEnregistrementDetail),
    };
  });
};

export const creerDetailCaptureVide = (idCapture: string): CaptureDetails => ({
  idCaptureDetail: Guid.newTemporary().toString(),
  idCapture,
  isDeleted: false,
} as unknown as CaptureDetails);

export const creerEmplacementVide = (
  idEnregistrementDetail: string,
  idEnregistrementEntete: Guid,
  chasse: boolean,
  idPointEauParDefaut?: string,
  idSecteurParDefaut?: string,
): Capture => {
  const idCapture = Guid.newTemporary().toString();

  return {
    idCapture,
    idEnregistrementDetail,
    idAuxiliaire: idEnregistrementEntete.toString(),
    idPointEau: chasse ? Guid.empty.toString() : (idPointEauParDefaut ?? Guid.empty.toString()),
    idSecteur: chasse ? (idSecteurParDefaut ?? Guid.empty.toString()) : Guid.empty.toString(),
    typeAuxiliaire: 'EN',
    capturesDetails: [creerDetailCaptureVide(idCapture)],
    isDeleted: false,
  } as unknown as Capture;
};

export const appliquerCapturesAEnregistrement = (
  row: IGestionEnregistrement,
  detailsCapture: EnregistrementDetails[],
  inclureCaptures = false,
): IGestionEnregistrement => ({
  ...row,
  enregistrementDetails: (row.enregistrementDetails ?? []).map((detail) => {
    const { captures: _captures, ...detailSansCaptures } = detail;

    if (!inclureCaptures) {
      return detailSansCaptures;
    }

    const detailCapture = detailsCapture.find(
      (captureDetail) => String(captureDetail.idEnregistrementDetail) === String(detail.idEnregistrementDetail),
    );

    if (!detailCapture || !(detailCapture.captures ?? []).length) {
      return detailSansCaptures;
    }

    const capturesUniques = dedupliquerCaptures(
      filtrerCapturesPourDetail(detailCapture.captures, detailCapture.idEnregistrementDetail),
    );

    return {
      ...detailSansCaptures,
      captures: capturesUniques.map((capture) => {
        const { capturesDetails, ...captureEntete } = capture;
        const details = capturesDetails ?? [];
        return {
          ...captureEntete,
          capturesDetails: details,
          detailsCapture: details.map((captureDetail) => ({
            ...captureDetail,
            idCapture: capture.idCapture,
            totalPrises: calculerPrisesDetail(captureDetail),
          })),
        };
      }),
    };
  }),
});

/**
 * Cette fonction réorganise les données des participants en fonction de leur nom complet,
 * et regroupe toutes les dates de participation d'un participant sous un seul objet.
 * Les participants exclus (indiqués par `exclus: true`) sont ignorés lorsque hideExcluded est true.
 * 
 * La fonction retourne un tableau d'objets contenant le nom complet du participant, 
 * ses dates de participation, et son identifiant d'accompagnateur.
 * 
 * @param {Array} data - Tableau de données, où chaque entrée contient une liste de participants et une date de participation.
 * @param {boolean} hideExcluded - Si true, les dates marquées exclus ne sont pas incluses.
 * @returns {Array} - Tableau d'objets réorganisés, chaque objet contient un participant, un tableau de dates et un identifiant d'accompagnateur.
 */
export const reorganizeData = (
  data: { isDeleted?: boolean; participants: { prenom: string; nom: string; exclus: boolean; idAccompagnateur: string | Guid; }[]; dateParticipation: string | Date; }[],
  hideExcluded: boolean = true,
) => {
  const participantsMap: { [key: string]: { dates: string[], dateKeys: Set<number>, nom: string, prenom: string, idAccompagnateur: string } } = {};

  data?.filter((entry) => !entry.isDeleted).forEach((entry) => {
    const participationDayKey = getParticipationDayKey(entry.dateParticipation);

    entry.participants?.forEach((participant) => {
      if (!participant.nom?.trim() && !participant.prenom?.trim()) {
        return;
      }

      const participantKey = `${participant.prenom} ${participant.nom}`;
      const idAccompagnateur = String(participant.idAccompagnateur ?? '');

      if (!participantsMap[participantKey]) {
        participantsMap[participantKey] = {
          dates: [],
          dateKeys: new Set<number>(),
          nom: participant.nom,
          prenom: participant.prenom,
          idAccompagnateur,
        };
      }

      if (!hideExcluded || !isParticipantExcluded(participant)) {
        if (participantsMap[participantKey].dateKeys.has(participationDayKey)) {
          return;
        }
        participantsMap[participantKey].dateKeys.add(participationDayKey);
        participantsMap[participantKey].dates.push(String(entry.dateParticipation));
      }
    });
  });

  return Object.entries(participantsMap)
    .map(([name, details]) => ({
      participant: name,
      nom: details.nom,
      prenom: details.prenom,
      dates: details.dates,
      idAccompagnateur: details.idAccompagnateur
    }));
};